import React from 'react';
import { BarChart3, Filter, Layers, LayoutDashboard, LayoutList, PanelRightClose, PanelRightOpen, Pin } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip';
import { GridExportCopyMenu } from './GridExportCopyMenu';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { QueryResultSet } from '../../../../types/queryExecution';

type ResultsGridActionsProps = {
  totalRows: number;
  /** True while rows are still populating (pre-columns spinner or mid-stream). Disables actions that need final data. */
  isLoading: boolean;
  showColumnProfiling: boolean;
  setShowColumnProfiling: (fn: (prev: boolean) => boolean) => void;
  showResultFilter: boolean;
  setShowResultFilter: (fn: (prev: boolean) => boolean) => void;
  setColumnFilters: (v: Record<string, string>) => void;
  setGlobalFilter: (v: string) => void;
  canFilter: boolean;
  isFilterActive: boolean;
  viewMode: string;
  onPin?: () => void;
  /** S20 — opens the pin-to-dashboard sheet; parent supplies provenance. */
  onPinToDashboard?: () => void;
  canExport: boolean;
  isWindowed: boolean;
  isExporting: boolean;
  copied: boolean;
  isCopyMenuOpen: boolean;
  setIsCopyMenuOpen: (open: boolean) => void;
  isExportMenuOpen: boolean;
  setIsExportMenuOpen: (open: boolean) => void;
  handleCopy: (format: 'csv' | 'markdown' | 'insert' | 'html') => Promise<void>;
  // `unknown` return: these handlers report an outcome for `ui_export_results`
  // (see useExportCopy.handleExport), which the menu itself has no use for.
  handleExport: (params: { format: 'csv' | 'json' | 'xlsx' | 'markdown' | 'sql'; scope: 'active' | 'all' }) => Promise<unknown>;
  handleExportAllCsv: () => Promise<unknown>;
  bufferRowCount: number;
  windowedTotalRowCount: number;
  remoteTotalRowCount: number | undefined;
  exportableResultSets: QueryResultSet[];
  handleToggleRowSidePanel: () => void;
  isRowSidePanelOpen: boolean;
  hasMultipleResultSets: boolean | undefined;
  setViewMode: (mode: 'tabs' | 'stack') => void;
  stackViewStatus: { available: boolean; reason: string | null };
};

export const ResultsGridActions = React.memo(function ResultsGridActions(props: ResultsGridActionsProps) {
  const {
    totalRows, isLoading, showColumnProfiling, setShowColumnProfiling,
    showResultFilter, setShowResultFilter, setColumnFilters, setGlobalFilter,
    canFilter, isFilterActive, viewMode, onPin, onPinToDashboard,
    canExport, isWindowed, isExporting, copied,
    isCopyMenuOpen, setIsCopyMenuOpen, isExportMenuOpen, setIsExportMenuOpen,
    handleCopy, handleExport, handleExportAllCsv,
    bufferRowCount, windowedTotalRowCount, remoteTotalRowCount, exportableResultSets,
    handleToggleRowSidePanel, isRowSidePanelOpen,
    hasMultipleResultSets, setViewMode, stackViewStatus,
  } = props;
  const { track } = useAnalytics();

  const loadingTooltip = 'Not available while loading';
  const windowedTooltip =
    'Column profiling is available for smaller results (under 50,000 rows and 20 MB). Narrow the query to enable profiling.';
  const filterDisabled = isLoading || totalRows === 0 || viewMode === 'stack' || !canFilter;
  const chartDisabled = isLoading || isWindowed || totalRows === 0;
  const pinDisabled = isLoading || totalRows === 0;

  return (
    <div className="flex items-center gap-1 ml-auto border-l border-border pl-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => {
              track('feature:results_column_profiling', { enabled: !showColumnProfiling });
              setShowColumnProfiling((prev) => !prev);
            }}
            className={`p-1.5 rounded-md transition-colors ${!chartDisabled ? 'text-muted-foreground hover:text-foreground hover:bg-background/50' : 'text-muted-foreground/40 cursor-not-allowed'} ${showColumnProfiling ? 'bg-background text-foreground shadow-sm' : ''}`}
            disabled={chartDisabled}
            data-testid="column-profiling-toggle"
          >
            <BarChart3 className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isLoading
            ? loadingTooltip
            : isWindowed
              ? windowedTooltip
              : "Column profiling. Click any column's profile bar for histogram and top values."}
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => {
              track('feature:results_filter', { enabled: !showResultFilter });
              setShowResultFilter((prev) => {
                if (prev) { setColumnFilters({}); setGlobalFilter(''); }
                return !prev;
              });
            }}
            className={`p-1.5 rounded-md transition-colors ${!filterDisabled ? 'text-muted-foreground hover:text-foreground hover:bg-background/50' : 'text-muted-foreground/40 cursor-not-allowed'} ${showResultFilter ? 'bg-background text-foreground shadow-sm' : ''} ${isFilterActive ? 'text-blue-500' : ''}`}
            disabled={filterDisabled}
            data-testid="result-filter-toggle"
          >
            <Filter className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isLoading
            ? loadingTooltip
            : canFilter ? 'Filter loaded rows (client-side)' : 'Filter not available for this result source'}
        </TooltipContent>
      </Tooltip>

      {onPin && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => { track('feature:results_pin'); onPin(); }}
              className={`p-1.5 rounded-md transition-colors ${!pinDisabled ? 'text-muted-foreground hover:text-foreground hover:bg-background/50' : 'text-muted-foreground/40 cursor-not-allowed'}`}
              disabled={pinDisabled}
              data-testid="pin-results-toggle"
            >
              <Pin className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{isLoading ? loadingTooltip : 'Pin current results'}</TooltipContent>
        </Tooltip>
      )}

      {onPinToDashboard && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onPinToDashboard}
              className={`p-1.5 rounded-md transition-colors ${!isLoading ? 'text-muted-foreground hover:text-foreground hover:bg-background/50' : 'text-muted-foreground/40 cursor-not-allowed'}`}
              disabled={isLoading}
              data-testid="pin-to-dashboard-button"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{isLoading ? loadingTooltip : 'Pin to dashboard'}</TooltipContent>
        </Tooltip>
      )}

      <GridExportCopyMenu
        canExport={canExport} isWindowed={isWindowed} isExporting={isExporting} copied={copied}
        isLoading={isLoading}
        isCopyMenuOpen={isCopyMenuOpen} setIsCopyMenuOpen={setIsCopyMenuOpen}
        isExportMenuOpen={isExportMenuOpen} setIsExportMenuOpen={setIsExportMenuOpen}
        handleCopy={handleCopy} handleExport={handleExport} handleExportAllCsv={handleExportAllCsv}
        bufferRowCount={bufferRowCount}
        windowedTotalRowCount={windowedTotalRowCount} remoteTotalRowCount={remoteTotalRowCount}
        exportableResultSets={exportableResultSets}
      />

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => {
              track('feature:results_row_side_panel', { enabled: !isRowSidePanelOpen });
              handleToggleRowSidePanel();
            }}
            className={`p-1.5 rounded-md transition-colors ${totalRows > 0 && viewMode !== 'stack' ? 'text-muted-foreground hover:text-foreground hover:bg-background/50' : 'text-muted-foreground/40 cursor-not-allowed'} ${isRowSidePanelOpen ? 'bg-background text-foreground shadow-sm' : ''}`}
            disabled={totalRows === 0 || viewMode === 'stack'}
            title={viewMode === 'stack' ? 'Row side panel is unavailable in stacked view' : 'Row side panel'}
            data-testid="row-side-panel-button"
          >
            {isRowSidePanelOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Row side panel</TooltipContent>
      </Tooltip>

      {hasMultipleResultSets && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => {
                  if (viewMode !== 'tabs') track('feature:results_view_mode', { mode: 'tabs' });
                  setViewMode('tabs');
                }}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'tabs' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                data-testid="view-mode-tabs-button"
              >
                <LayoutList className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Tabbed view</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <button
                  onClick={() => {
                    if (!stackViewStatus.available) return;
                    if (viewMode !== 'stack') track('feature:results_view_mode', { mode: 'stack' });
                    setViewMode('stack');
                  }}
                  disabled={!stackViewStatus.available}
                  className={`p-1.5 rounded-md transition-colors ${!stackViewStatus.available ? 'text-muted-foreground/40 cursor-not-allowed' : viewMode === 'stack' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  data-testid="view-mode-stack-button"
                >
                  <Layers className="h-3.5 w-3.5" />
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" data-testid="stack-view-tooltip">
              {stackViewStatus.available ? 'Stacked view' : `Stack view unavailable: ${stackViewStatus.reason}`}
            </TooltipContent>
          </Tooltip>
        </>
      )}
    </div>
  );
});
